import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  HStack,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Icon,
  Flex,
  ButtonGroup,
  UnorderedList,
  ListItem,
  Select,
  Heading,
  Tooltip
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, ViewIcon, EditIcon, QuestionIcon } from '@chakra-ui/icons';
import { FaArchive, FaPaperPlane, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';

const getUserFullName = (userId, userProfiles) => {
  if (!userId) return '-';
  const profile = userProfiles[userId];
  if (!profile) return '-';
  return `${profile.first_name} ${profile.last_name}`;
};

// Help content for message templates
export const messageTemplatesHelpContent = (
  <>
    <Text mb={4}>
      The Message Templates page allows you to manage predefined SMS templates for consistent messaging across your organization.
    </Text>

    <Accordion allowMultiple>
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              Templates Overview
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <UnorderedList spacing={1}>
            <ListItem>System Templates: Pre-defined templates for core functionality (admin only)</ListItem>
            <ListItem>User Templates: Custom templates for common messages</ListItem>
            <ListItem>All templates support basic text formatting</ListItem>
          </UnorderedList>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              Search and Filtering
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <UnorderedList spacing={1}>
            <ListItem>Use the search bar to filter templates by name</ListItem>
            <ListItem>Click column headers to sort by:
              <UnorderedList>
                <ListItem>Template Name</ListItem>
                <ListItem>Last Modified Date</ListItem>
                <ListItem>Last Modified By</ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              Template Management
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <UnorderedList spacing={1}>
            <ListItem>Click + to create a new template</ListItem>
            <ListItem>Preview (eye): View the template as it will appear</ListItem>
            <ListItem>Edit (pencil): Modify template content and settings</ListItem>
            <ListItem>Test (paper plane): Send a test message to yourself</ListItem>
          </UnorderedList>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              Template Categories
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <UnorderedList spacing={1}>
            <ListItem>System Templates (Admin Only):
              <UnorderedList>
                <ListItem>Consent Request: For requesting messaging consent</ListItem>
                <ListItem>Opt-out Confirmation: Sent when a user opts out</ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem>User Templates:
              <UnorderedList>
                <ListItem>Custom templates for regular communications</ListItem>
                <ListItem>Can be created and modified by any user</ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              Best Practices
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <UnorderedList spacing={1}>
            <ListItem>Keep messages clear and concise</ListItem>
            <ListItem>Always test templates before using them</ListItem>
            <ListItem>Review and update templates regularly</ListItem>
            <ListItem>Consider character limits for SMS messages</ListItem>
          </UnorderedList>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  </>
);

const ManageMessageTemplates = () => {
  const toast = useToast();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isHelpOpen, onOpen: onHelpOpen, onClose: onHelpClose } = useDisclosure();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [formData, setFormData] = useState({
    template_name: '',
    content: '',
    template_type: 'sms',
    template_category: 'User',
  });
  const [previewContent, setPreviewContent] = useState('');
  const [testData, setTestData] = useState({
    name: '',
    phone: '',
    email: '',
    clubName: ''
  });
  const [userProfiles, setUserProfiles] = useState({});
  const [templateTypeFilter, setTemplateTypeFilter] = useState('sms');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchUserProfiles = async () => {
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name');
      
      if (error) throw error;

      // Convert array to object for easy lookup
      const profileMap = {};
      profiles.forEach(profile => {
        profileMap[profile.id] = profile;
      });
      setUserProfiles(profileMap);
    } catch (error) {
      console.error('Error fetching user profiles:', error);
    }
  };

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_administrator')
          .eq('id', user.id)
          .single();
          
        setIsAdmin(profile?.is_administrator || false);
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    fetchUserProfiles();
  }, []);

  const loadUserDefaults = async () => {
    try {
      // Get user info and profile
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone_number')
        .eq('id', user.id)
        .single();

      if (user && profile) {
        setTestData({
          ...testData,
          name: `${profile.first_name} ${profile.last_name}`,
          email: user.email,
          phone: profile.phone_number || ''
        });
      }

      // Get club name from preferences
      const { data: preferences } = await supabase
        .from('club_preferences')
        .select('club_name')
        .single();

      if (preferences?.club_name) {
        setTestData(prev => ({
          ...prev,
          clubName: preferences.club_name
        }));
      }
    } catch (error) {
      console.error('Error loading user defaults:', error);
    }
  };

  // Load templates
  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('message_templates_with_users')
        .select('*')
        .order('template_name');

      if (error) throw error;

      setTemplates(data || []);
      setFilteredTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error loading templates',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadUserDefaults();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [isAdmin]);

  // Search and sort effects
  useEffect(() => {
    if (!templates) return;

    let filtered = [...templates];

    // Apply template type filter
    if (templateTypeFilter) {
      filtered = filtered.filter(template => template.template_type === templateTypeFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(template => {
        if (statusFilter === 'active') {
          return !template.archived_at;
        } else {
          return !!template.archived_at;
        }
      });
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(template =>
        template.template_name.toLowerCase().includes(query) ||
        template.content.toLowerCase().includes(query) ||
        template.template_type.toLowerCase().includes(query) ||
        template.version.toString().includes(query) ||
        (template.modified_by_name && template.modified_by_name.toLowerCase().includes(query)) ||
        (template.archived_at ? 'archived' : 'active').includes(query)
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'archived_at') {
          aValue = a.archived_at ? 'Archived' : 'Active';
          bValue = b.archived_at ? 'Archived' : 'Active';
        }

        // Handle null values
        if (aValue === null) return 1;
        if (bValue === null) return -1;

        // Convert dates to timestamps for comparison
        if (sortConfig.key.includes('_at')) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    setFilteredTemplates(filtered);
  }, [templates, searchQuery, templateTypeFilter, statusFilter, sortConfig]);

  const requestSort = (key) => {
    if (sortConfig.key === key) {
      setSortConfig({
        key,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      setSortConfig({
        key,
        direction: 'asc'
      });
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      template_name: template.template_name,
      content: template.content,
      template_type: template.template_type,
      template_category: template.template_category,
    });
    onEditOpen();
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setPreviewContent(template.content);
    onPreviewOpen();
  };

  const handleNew = () => {
    setSelectedTemplate(null);
    setFormData({
      template_name: '',
      content: '',
      template_type: 'sms',
      template_category: 'User',
    });
    onEditOpen();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const templateData = {
        template_name: formData.template_name,
        content: formData.content,
        template_type: formData.template_type,
        template_category: isAdmin ? formData.template_category : 'User',
        version: 1,
        created_by: user.id,
        updated_by: user.id,
      };

      if (selectedTemplate) {
        // Update
        const { error } = await supabase
          .from('message_templates')
          .update({
            ...templateData,
            version: selectedTemplate.version + 1,
            updated_by: user.id,
          })
          .eq('id', selectedTemplate.id);
          
        if (error) throw error;
      } else {
        // Insert
        const { error } = await supabase
          .from('message_templates')
          .insert([templateData]);
          
        if (error) throw error;
      }

      onEditClose();
      loadTemplates();
      toast({
        title: `Template ${selectedTemplate ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error saving template',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleTestMessage = async (template) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('phone')
        .eq('id', user.id)
        .single();

      if (!profile?.phone) {
        toast({
          title: 'No phone number found',
          description: 'Please add a phone number to your profile to receive test messages.',
          status: 'warning',
          duration: 5000,
        });
        return;
      }

      // Call your SMS sending function here
      // For now, just show a success toast
      toast({
        title: 'Test message sent',
        description: `A test message would be sent to ${profile.phone}`,
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error sending test message:', error);
      toast({
        title: 'Error sending test message',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleArchive = async (template) => {
    try {
      const { error } = await supabase
        .from('message_templates')
        .update({
          archived_at: template.archived_at ? null : new Date(),
        })
        .eq('id', template.id);
        
      if (error) throw error;

      loadTemplates();
      toast({
        title: `Template ${template.archived_at ? 'restored' : 'archived'} successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error archiving template:', error);
      toast({
        title: 'Error archiving template',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Box p={4}>
      {/* Header section with title, help icon, and filters */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Flex alignItems="center">
          <Heading size="md">Manage Message Templates</Heading>
          <Box ml="100px">
            <IconButton
              icon={<AddIcon />}
              colorScheme="blue"
              size="sm"
              aria-label="Create Template"
              onClick={handleNew}
            />
          </Box>
        </Flex>

        <Tooltip 
          label={messageTemplatesHelpContent}
          hasArrow
          placement="left"
          maxW="400px"
        >
          <IconButton
            icon={<QuestionIcon />}
            variant="ghost"
            size="sm"
            aria-label="Help"
          />
        </Tooltip>
      </Flex>

      {/* Search and filters row */}
      <Flex mb={4} alignItems="center" gap={4}>
        <InputGroup maxW="800px" flex={1}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by template name, content, template type, version, last modified, modified by, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
        
        <Flex ml="auto" gap={4}>
          <Select 
            w="250px"
            value={templateTypeFilter}
            onChange={(e) => setTemplateTypeFilter(e.target.value)}
          >
            <option value="sms">SMS</option>
          </Select>
          
          <Select 
            w="250px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Templates</option>
            <option value="active">Active Templates</option>
            <option value="archived">Archived Templates</option>
          </Select>
        </Flex>
      </Flex>

      {/* Templates table */}
      <Box 
        flex={1} 
        minHeight="0" 
        borderY="1px" 
        borderColor="gray.200" 
        mb={4} 
        overflowY="auto"
        maxHeight="calc(100vh - 400px)"
      >
        <Table variant="simple" size="sm" layout="fixed">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('template_name')}
                width="250px"
              >
                Template Name
                <Icon
                  as={sortConfig.key === 'template_name' 
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('content')}
                width="400px"
              >
                Content
                <Icon
                  as={sortConfig.key === 'content'
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('template_type')}
              >
                Template Type
                <Icon
                  as={sortConfig.key === 'template_type'
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('version')}
              >
                Version
                <Icon
                  as={sortConfig.key === 'version'
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('updated_at')}
              >
                Last Modified
                <Icon
                  as={sortConfig.key === 'updated_at'
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('modified_by_name')}
              >
                Modified By
                <Icon
                  as={sortConfig.key === 'modified_by_name'
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th 
                cursor="pointer"
                onClick={() => requestSort('archived_at')}
              >
                Status
                <Icon
                  as={sortConfig.key === 'archived_at'
                    ? (sortConfig.direction === 'asc' ? FaSortUp : FaSortDown)
                    : FaSort}
                  ml={2}
                />
              </Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredTemplates.map((template) => (
              <Tr key={template.id} sx={{ 
                'td': { 
                  py: 1  // Reduce vertical padding by 50%
                }
              }}>
                <Td
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  fontWeight="bold"
                >
                  {template.template_name}
                </Td>
                <Td maxW="400px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {template.content}
                </Td>
                <Td>{template.template_type}</Td>
                <Td>{template.version}</Td>
                <Td>{template.updated_at ? format(new Date(template.updated_at), 'MM/dd/yyyy HH:mm:ss') : '-'}</Td>
                <Td>{template.modified_by_name || '-'}</Td>
                <Td>{template.archived_at ? 'Archived' : 'Active'}</Td>
                <Td>
                  <ButtonGroup spacing={2} size="sm">
                    <IconButton
                      aria-label="Edit template"
                      icon={<EditIcon />}
                      onClick={() => handleEdit(template)}
                    />
                    <IconButton
                      aria-label="Preview template"
                      icon={<ViewIcon />}
                      onClick={() => handlePreview(template)}
                    />
                    <IconButton
                      aria-label="Send test message"
                      icon={<FaPaperPlane />}
                      onClick={() => handleTestMessage(template)}
                    />
                    <IconButton
                      aria-label={template.archived_at ? "Restore template" : "Archive template"}
                      icon={<FaArchive />}
                      onClick={() => handleArchive(template)}
                    />
                  </ButtonGroup>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Test Settings Section - Fixed at bottom */}
      <Box flex="0 0 auto">
        <Text fontSize="sm" fontWeight="bold" mb={1}>
          Test Settings
        </Text>
        <Text fontSize="xs" color="gray.600" mb={2}>
          These settings are used to preview and test your message templates with sample data.
        </Text>
        
        <SimpleGrid columns={4} spacing={4}>
          <FormControl size="sm">
            <FormLabel fontSize="xs">First and Last Name</FormLabel>
            <Input
              size="sm"
              value={testData.name}
              onChange={(e) => setTestData({ ...testData, name: e.target.value })}
              placeholder="Enter name"
            />
          </FormControl>
          
          <FormControl size="sm">
            <FormLabel fontSize="xs">Phone Number</FormLabel>
            <Input
              size="sm"
              value={testData.phone}
              onChange={(e) => setTestData({ ...testData, phone: e.target.value })}
              placeholder="Enter phone number"
            />
          </FormControl>
          
          <FormControl size="sm">
            <FormLabel fontSize="xs">Email</FormLabel>
            <Input
              size="sm"
              value={testData.email}
              onChange={(e) => setTestData({ ...testData, email: e.target.value })}
              placeholder="Enter email"
            />
          </FormControl>
          
          <FormControl size="sm">
            <FormLabel fontSize="xs">Club Name</FormLabel>
            <Input
              size="sm"
              value={testData.clubName}
              onChange={(e) => setTestData({ ...testData, clubName: e.target.value })}
              placeholder="Enter club name"
            />
          </FormControl>
        </SimpleGrid>
      </Box>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedTemplate ? 'Edit Template' : 'New Template'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Template Name</FormLabel>
                <Input
                  value={formData.template_name}
                  onChange={(e) =>
                    setFormData({ ...formData, template_name: e.target.value })
                  }
                  placeholder="Enter template name"
                  maxLength={50}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Content</FormLabel>
                <Textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  placeholder="Enter content"
                  rows={6}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Template Type</FormLabel>
                <Select
                  value={formData.template_type}
                  onChange={(e) =>
                    setFormData({ ...formData, template_type: e.target.value })
                  }
                >
                  <option value="sms">SMS</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Template Category</FormLabel>
                <Select
                  value={formData.template_category}
                  onChange={(e) =>
                    setFormData({ ...formData, template_category: e.target.value })
                  }
                  isDisabled={!isAdmin}
                >
                  <option value="User">User</option>
                  <option value="System">System</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Preview Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text whiteSpace="pre-wrap">{previewContent}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onPreviewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal 
        isOpen={isHelpOpen} 
        onClose={onHelpClose} 
        size="lg"
      >
        <ModalOverlay />
        <ModalContent
          maxH="calc(100vh - 100px)"
          h="600px"
        >
          <ModalHeader>Manage Message Templates Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody 
            pb={6}
            overflowY="auto"
          >
            <VStack align="stretch" spacing={4}>
              {messageTemplatesHelpContent}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ManageMessageTemplates;
